import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import Icon from '@/common/components/dataDisplay/Icon';
import React, {
  Fragment,
  useRef,
  useState,
} from 'react';
import { Copy, ExternalLink, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from 'reactstrap';

import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import Btn from '@/components/dataDisplay/Btn';
import Tooltip from '@/components/dataDisplay/Tooltip';

type Props = {
  link: string;
}

const ShareButton: React.FunctionComponent<Props> = (props): JSX.Element => {
  const { link } = props;
  const [isPreview, setIsPreview] = useState(true);
  const [isTextCopied, setIsTextCopied] = useState(false);
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const theme: CustomerTheme = useTheme();

  /**
   * Copy the content of a textarea into the clipboard
   *
   * @see https://stackoverflow.com/questions/39501289/in-reactjs-how-to-copy-text-to-clipboard
   */
  const copyToClipboard = (logEvent: LogEvent): void => {
    inputRef.current.select();

    if (document.queryCommandSupported('copy')) {
      document.execCommand('copy');
      setIsTextCopied(true);

      logEvent('solutions-shareBtn-clicked', {
        action: 'click-copy',
      });
    } else {
      setIsTextCopied(false);

      logEvent('solutions-shareBtn-clicked', {
        action: 'click-copy-failed',
      });
    }
  };

  if (isPreview) {
    return (
      <Amplitude
        eventProperties={(inheritedProps): GenericObject => ({
          ...inheritedProps,
        })}
      >
        {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
          <Fragment>
            <Btn
              onClick={(): void => {
                setIsPreview(false);

                logEvent('solutions-shareBtn-clicked', {
                  action: 'click-preview',
                });

                setTimeout(() => {
                  inputRef.current.click();
                }, 300);
              }}
            >
              <Icon icon={ExternalLink} />
              {t('solutionsPage.solutionsHeader.previewShareBtn.label', 'Partager')}
            </Btn>
            {' '}
            <Tooltip
              overlay={(
                <span>
                  {t('solutionsPage.solutionsHeader.previewShareBtn.help', `Vous pouvez partager le lien de votre simulation à vos proches.`)}
                </span>
              )}
            >
              <Icon icon={Info} variant="light" />
            </Tooltip>
          </Fragment>
        )}
      </Amplitude>
    );
  } else {
    return (
      <Amplitude
        eventProperties={(inheritedProps): GenericObject => ({
          ...inheritedProps,
        })}
      >
        {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
          <Tooltip
            overlay={
              <span>
                {
                  isTextCopied ?
                    t('solutionsPage.solutionsHeader.copyShareBtn.tooltipSuccess', `Le lien a été copié dans votre presse papier`) :
                    t('solutionsPage.solutionsHeader.copyShareBtn.tooltipFailed', `Votre navigateur ne permet pas de copier le lien dans votre presse papier, veuillez le copier manuellement`)
                }
              </span>
            }
            css={css`
              .share-icon {
                color: white;
              }

              .input-group-text {
                background-color: ${theme.primaryColor};
              }
            `}
            placement={'left'}
          >
            <InputGroup
              onClick={(): void => {
                copyToClipboard(logEvent);
              }}
            >
              <Input
                placeholder={link}
                value={link}
                innerRef={inputRef}
                readOnly={true}
                onChange={null} // Necessary to avoid useless warning about controlled/uncontrolled react component
              />
              <InputGroupAddon
                addonType="append"
                className={'button'}
              >
                <InputGroupText>
                  <Icon
                    className={'share-icon'}
                    icon={Copy}
                  />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </Tooltip>
        )}
      </Amplitude>
    );
  }
};

export default ShareButton;
